import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";

/**
 * Iter-158.13.2.20f.2 #5 — Persistent H&M backup warning banner.
 *
 * Renders on H&M surfaces (H&M hub / OnSet / Fittings) when the
 * Production has a canonical script AND its external backup is
 * unconfigured (`backup==="none"`) AND the caller can manage
 * backups (Owner / Production Admin — the same 403 gate the
 * ProjectHeader chip uses). No new engine, no new endpoint — the
 * banner probes `/backup/connection` exactly the way
 * `ProjectHeader.loadBackupStatus` does today; a 403 for members
 * silently hides the banner.
 *
 * The banner never renders on the legacy `/projects/*` mount or on
 * the Production `/beta/projects/*` mount — only on the H&M dept
 * mount, because H&M is the working destination most likely to be
 * open all day while script drafts get replaced. Producers who
 * live in H&M shouldn't have to remember to open Storage to notice
 * their backup was never configured.
 *
 * Props are entirely optional:
 *   - projectId: the Production id (from the URL param — the caller
 *     already has it via useParams()).
 *   - scriptPresent: truthy signal from the calling page that the
 *     Production has a canonical script uploaded. If falsy the
 *     banner stays hidden (no script → nothing to protect yet).
 */
export default function HMBackupBanner({ projectId, scriptPresent }) {
    const apiBase = useApiBase();
    const [status, setStatus] = useState(null); // 'none' | 'paused' | 'active' | null
    const [canManage, setCanManage] = useState(false);

    const isHmMount = typeof apiBase === "string"
        && apiBase.startsWith("/beta/hm-app/projects");

    useEffect(() => {
        if (!isHmMount || !projectId) {
            setStatus(null);
            setCanManage(false);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                // Backups live at Production scope. The H&M dept mount
                // does NOT expose `/backup/connection` — we reach
                // across to the Production mount deliberately so this
                // banner sees the same truth ProjectSettings sees.
                const { data } = await api.get(
                    `/beta/projects/${projectId}/backup/connection`,
                );
                if (cancelled) return;
                setCanManage(true);
                if (!data?.connection) setStatus("none");
                else if (!data.connection.enabled) setStatus("paused");
                else setStatus("active");
            } catch {
                if (cancelled) return;
                // 403 for non-admin members OR transient failure — hide
                setCanManage(false);
                setStatus(null);
            }
        })();
        return () => { cancelled = true; };
    }, [isHmMount, projectId]);

    if (!isHmMount) return null;
    if (!scriptPresent) return null;
    if (!canManage) return null;
    if (status !== "none") return null;

    return (
        <div
            data-testid="hm-backup-warning-banner"
            className="max-w-2xl mx-auto mt-3 px-5"
        >
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/20 dark:bg-amber-500/10 p-3 flex items-start gap-3">
                <AlertTriangle size={16} className="text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-amber-900 dark:text-amber-100">
                        No external backup on this Production
                    </div>
                    <p className="text-[11px] text-amber-900/90 dark:text-amber-100/80 mt-0.5 leading-relaxed">
                        Your canonical script is uploaded but nothing is being mirrored to your own storage. Connect Dropbox (or another provider) so continuity captures survive a device loss.
                    </p>
                </div>
                <Link
                    data-testid="hm-backup-warning-cta"
                    to={`/beta/projects/${projectId}/backups`}
                    className="tap shrink-0 px-3 h-8 inline-flex items-center rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold bg-amber-500 text-neutral-950 border border-amber-500 hover:bg-amber-400"
                >
                    Set Up Backup
                    <ArrowRight size={12} className="ml-1" />
                </Link>
            </div>
        </div>
    );
}
